import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { invoicesApi } from '../api/invoices';
import { partiesApi } from '../api/parties';
import type { Invoice } from '@coreledger/shared-types';
import Pagination from '../components/Pagination';
import { confirm } from '../components/ConfirmDialog';
import { fmt, STATUS_BADGE, DOC_TYPE_LABELS } from '../lib/utils';
import { useRole } from '../context/AuthContext';
import RecordPaymentModal from '../components/RecordPaymentModal';

function buildReminderMsg(inv: Invoice): string {
  const balance = inv.total - (inv.paidAmount ?? 0);
  return `Dear ${inv.partyName},\n\nThis is a friendly reminder that invoice ${inv.invoiceNumber ?? ''} for ${fmt(balance)} is overdue${inv.dueDate ? ` since ${inv.dueDate}` : ''}.\n\nKindly arrange payment at the earliest.\n\nThank you!`;
}

export default function InvoicesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { canDelete } = useRole();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [docType, setDocType] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [payModal, setPayModal] = useState<{ invoice: Invoice } | null>(null);

  // Debounce search — don't fire a request on every keystroke
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', status, docType, debouncedSearch, page],
    queryFn: () => invoicesApi.list({
      status: status || undefined,
      docType: docType || undefined,
      search: debouncedSearch || undefined,
      excludeLorryReceipts: true,
    }, page, 50),
    staleTime: 0,
  });
  const invoices = data?.data ?? [];
  const meta = data?.meta;

  const { data: partiesData } = useQuery({ queryKey: ['parties'], queryFn: () => partiesApi.list(), staleTime: 30 * 1000 });
  const parties = partiesData?.data ?? [];

  const del = useMutation({
    mutationFn: invoicesApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.refetchQueries({ queryKey: ['sales-summary'], type: 'all' });
      qc.refetchQueries({ queryKey: ['outstanding'], type: 'all' });
      qc.refetchQueries({ type: 'all' });
      setDeletingId(null);
    },
    onError: () => setDeletingId(null),
  });

  const bulkMarkPaid = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => invoicesApi.update(id, { status: 'paid' })));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.refetchQueries({ queryKey: ['sales-summary'], type: 'all' });
      qc.refetchQueries({ queryKey: ['outstanding'], type: 'all' });
      qc.refetchQueries({ type: 'all' });
      setSelected(new Set());
    },
  });

  async function handleDelete(inv: Invoice) {
    const ok = await confirm({ message: `Delete ${inv.invoiceNumber || 'this invoice'}?`, danger: true });
    if (!ok) return;
    setDeletingId(inv.id);
    del.mutate(inv.id);
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Invoices</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {selected.size > 0 && (
            <button
              className="btn btn-success btn-sm"
              disabled={bulkMarkPaid.isPending}
              onClick={async () => {
                if (await confirm({ message: `Mark ${selected.size} invoice(s) as paid?` })) {
                  bulkMarkPaid.mutate([...selected]);
                }
              }}
            >
              {bulkMarkPaid.isPending ? <span className="spinner" style={{ width: 12, height: 12 }} /> : `✓ Mark Paid (${selected.size})`}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => navigate('/invoices/new')}>+ New Invoice</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by invoice # or party…"
          style={{ maxWidth: 280 }}
        />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
        <select value={docType} onChange={e => { setDocType(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
          <option value="">All types</option>
          <option value="tax_invoice">Tax Invoice</option>
          <option value="bill_of_supply">Bill of Supply</option>
          <option value="quotation">Quotation</option>
          <option value="credit_note">Credit Note</option>
          <option value="debit_note">Debit Note</option>
        </select>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="loading-overlay"><span className="spinner" /></div>
        ) : invoices.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🧾</div>
            <div className="empty-state-text">No invoices found</div>
            <div style={{ marginTop: '0.75rem' }}>
              <button className="btn btn-primary" onClick={() => navigate('/invoices/new')}>+ Create Invoice</button>
            </div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                    checked={invoices.length > 0 && invoices.every(i => selected.has(i.id))}
                    onChange={e => setSelected(e.target.checked ? new Set(invoices.map(i => i.id)) : new Set())}
                  />
                </th>
                <th>Invoice #</th><th>Date</th><th>Due Date</th><th>Party</th><th>Type</th>
                <th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Paid</th>
                <th>Status</th><th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} style={{ background: selected.has(inv.id) ? 'var(--primary-light)' : undefined }}>
                  <td>
                    <input type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                      checked={selected.has(inv.id)}
                      onChange={e => setSelected(prev => { const s = new Set(prev); e.target.checked ? s.add(inv.id) : s.delete(inv.id); return s; })}
                    />
                  </td>
                  <td>
                    <span style={{ fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline dotted' }}
                      onClick={() => navigate(`/invoices/${inv.id}`)}>
                      {inv.invoiceNumber || '(draft)'}
                    </span>
                  </td>
                  <td>{inv.date}</td>
                  <td style={{ color: inv.status === 'overdue' ? 'var(--danger)' : undefined }}>{inv.dueDate ?? '—'}</td>
                  <td>{inv.partyName}</td>
                  <td><span className="badge badge-blue">{DOC_TYPE_LABELS[inv.docType ?? ''] ?? inv.docType}</span></td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(inv.total)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--success)' }}>{fmt(inv.paidAmount ?? 0)}</td>
                  <td><span className={`badge ${STATUS_BADGE[inv.status] ?? 'badge-gray'}`}>{inv.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/invoices/${inv.id}/edit`)}>Edit</button>
                      {(inv.status === 'unpaid' || inv.status === 'overdue') && inv.docType !== 'lorry_receipt' && (
                        <button className="btn btn-ghost btn-sm" title="Record Payment" onClick={() => setPayModal({ invoice: inv })}>+ Rcpt</button>
                      )}
                      {inv.status === 'overdue' && (() => {
                        const phone = parties.find(p => p.id === inv.partyId)?.phone?.replace(/\D/g, '') ?? '';
                        return (
                          <a
                            href={`https://wa.me/${phone}?text=${encodeURIComponent(buildReminderMsg(inv))}`}
                            target="_blank" rel="noreferrer"
                            className="btn btn-ghost btn-sm"
                            style={{ color: '#16a34a', borderColor: '#86efac' }}
                            title="Send WhatsApp payment reminder"
                          >📲</a>
                        );
                      })()}
                      {canDelete && (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={deletingId === inv.id}
                          onClick={() => handleDelete(inv)}
                        >
                          {deletingId === inv.id ? <span className="spinner" style={{ width: 10, height: 10 }} /> : 'Del'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {meta && meta.totalPages > 1 && (
          <Pagination page={page} totalPages={meta.totalPages} onChange={setPage} />
        )}
      </div>

      {payModal && (
        <RecordPaymentModal
          invoiceId={payModal.invoice.id}
          invoiceNumber={payModal.invoice.invoiceNumber ?? undefined}
          balanceDue={payModal.invoice.total - (payModal.invoice.paidAmount ?? 0)}
          onClose={() => setPayModal(null)}
        />
      )}
    </div>
  );
}
